import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import type { FilterAst } from "../../core/filter";
import { COMPUTED_KEYS, computeAll } from "../../core/computed";
import { setProp } from "../../core/ops";
import type { Task } from "../../core/schema";
import { FILTER_SCHEMA, visibleTasks } from "../shared";
import { AXIS_DEFAULTS, dotColour } from "./axisColor";
import { AxisLegend } from "./AxisLegend";
import { axisRange } from "./range";

export interface Axis {
  prop?: string;
  min?: number;
  max?: number;
  label?: string;
}

export interface Eisenhower2Config {
  filter?: FilterAst;
  xProp?: string;
  xMin?: number;
  xMax?: number;
  xLabel?: string;
  /** "fit to data": the axis runs from the smallest to the largest value shown */
  xFit?: boolean;
  yProp?: string;
  yMin?: number;
  yMax?: number;
  yLabel?: string;
  yFit?: boolean;
  quadrants?: string[];
  dotSize?: "estimate" | "fixed";
  /** "axes": every axis has a low and a high colour, dots mix them by their place */
  colorBy?: "axes" | "none";
  xColorMin?: string;
  xColorMax?: string;
  yColorMin?: string;
  yColorMax?: string;
}

const isComputed = (p: string) => (COMPUTED_KEYS as readonly string[]).includes(p);

interface Dot {
  task: Task;
  x: number;
  y: number;
  r: number;
}

/**
 * Two-axis scatter (spec §9.7 `eisenhower2`): any numeric or computed prop per axis; dragging a dot
 * writes the props (disabled for computed axes). Arrow keys move a focused dot by one step.
 */
export function Eisenhower2View({ config, ctx }: ViewProps<Eisenhower2Config>) {
  const { t } = useTranslation();
  const xProp = config.xProp ?? "energy";
  const yProp = config.yProp ?? "want";
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const minute = Math.floor(ctx.now / 60_000) * 60_000;

  const dots =
    useDocSelector(
      ctx.handle,
      (doc): Dot[] => {
        const v = visibleTasks(doc, config.filter, minute);
        const val = (task: Task, prop: string): number | null => {
          const raw = isComputed(prop)
            ? (computeAll(v.snapshot, task.id, minute) as Record<string, unknown>)[prop]
            : task.props[prop];
          return typeof raw === "number" ? raw : typeof raw === "boolean" ? (raw ? 1 : 0) : null;
        };
        const out: Dot[] = [];
        for (const task of v.tasks) {
          const x = val(task, xProp) ?? (xProp === "energy" ? 3 : null);
          const y = val(task, yProp);
          if (x === null || y === null) continue;
          const est = typeof task.props.estimateMin === "number" ? task.props.estimateMin : 30;
          out.push({
            task,
            x,
            y,
            r: config.dotSize === "fixed" ? 6 : Math.max(4, Math.min(14, 3 + Math.sqrt(est))),
          });
        }
        return out;
      },
      [config, minute],
    ) ?? [];

  // with "fit to data" the range follows the dots, but not while one is being dragged (no surprises, §9.19)
  const fitted = {
    x: axisRange(
      dots.map((d) => d.x),
      config.xFit,
      config.xMin ?? 1,
      config.xMax ?? 5,
    ),
    y: axisRange(
      dots.map((d) => d.y),
      config.yFit,
      config.yMin ?? 0,
      config.yMax ?? 3,
    ),
  };
  const [held, setHeld] = useState<typeof fitted | null>(null);
  const range = held ?? fitted;
  const xMin = range.x.min;
  const xMax = range.x.max;
  const yMin = range.y.min;
  const yMax = range.y.max;

  const W = 600;
  const H = 400;
  const pad = 36;
  const sx = (x: number) =>
    pad + ((Math.min(xMax, Math.max(xMin, x)) - xMin) / (xMax - xMin || 1)) * (W - 2 * pad);
  const sy = (y: number) =>
    H - pad - ((Math.min(yMax, Math.max(yMin, y)) - yMin) / (yMax - yMin || 1)) * (H - 2 * pad);
  const inv = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * W;
    const py = ((clientY - rect.top) / rect.height) * H;
    const x = xMin + ((px - pad) / (W - 2 * pad)) * (xMax - xMin);
    const y = yMin + ((H - pad - py) / (H - 2 * pad)) * (yMax - yMin);
    return { x: Math.min(xMax, Math.max(xMin, x)), y: Math.min(yMax, Math.max(yMin, y)) };
  };
  const draggable = !ctx.readOnly && !isComputed(xProp) && !isComputed(yProp);
  const write = (id: string, x: number, y: number) =>
    ctx.write((doc) => {
      if (!isComputed(xProp)) setProp(doc, id, xProp, Math.round(x * 2) / 2);
      if (!isComputed(yProp)) setProp(doc, id, yProp, Math.round(y * 2) / 2);
    });
  const q = config.quadrants ?? [];
  // plain colours (a11y) win: then one muted colour, position alone carries the meaning
  const plain = document.documentElement.dataset.plainColors === "true";
  const axisColours =
    config.colorBy !== "none" && !plain
      ? [
          { low: config.xColorMin ?? AXIS_DEFAULTS.x.low, high: config.xColorMax ?? AXIS_DEFAULTS.x.high },
          { low: config.yColorMin ?? AXIS_DEFAULTS.y.low, high: config.yColorMax ?? AXIS_DEFAULTS.y.high },
        ]
      : null;
  const colourOf = (x: number, y: number) =>
    (axisColours &&
      dotColour(axisColours, [(x - xMin) / (xMax - xMin || 1), (y - yMin) / (yMax - yMin || 1)])) ||
    "var(--color-accent)";

  return (
    <div className="kss-surface flex h-full flex-col gap-1 p-2" data-testid="eisenhower2-view">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="min-h-0 w-full flex-1 touch-none select-none"
        role="group"
        aria-label={t("views.eisenhower2.label")}
        onPointerMove={(e) => drag && setDrag({ id: drag.id, ...inv(e.clientX, e.clientY) })}
        onPointerUp={() => {
          if (drag) write(drag.id, drag.x, drag.y);
          setDrag(null);
          setHeld(null);
        }}
      >
        <rect
          x={pad}
          y={pad}
          width={W - 2 * pad}
          height={H - 2 * pad}
          fill="var(--surface-2)"
          stroke="var(--line)"
        />
        <line x1={W / 2} y1={pad} x2={W / 2} y2={H - pad} stroke="var(--line-strong)" strokeDasharray="4 4" />
        <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke="var(--line-strong)" strokeDasharray="4 4" />
        {[
          [pad + 8, pad + 16, q[0]],
          [W / 2 + 8, pad + 16, q[1]],
          [pad + 8, H / 2 + 16, q[2]],
          [W / 2 + 8, H / 2 + 16, q[3]],
        ].map(
          ([x, y, label], i) =>
            label && (
              <text
                key={i}
                x={x as number}
                y={y as number}
                fontSize="11"
                fill="var(--color-neutral-400)"
                fontFamily="var(--font-mono)"
              >
                {label as string}
              </text>
            ),
        )}
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="12" fill="var(--color-neutral-500)">
          {config.xLabel || xProp} →
        </text>
        <text
          x={12}
          y={H / 2}
          textAnchor="middle"
          fontSize="12"
          fill="var(--color-neutral-500)"
          transform={`rotate(-90 12 ${H / 2})`}
        >
          {config.yLabel || yProp} →
        </text>
        {dots.map((d) => {
          const x = drag?.id === d.task.id ? drag.x : d.x;
          const y = drag?.id === d.task.id ? drag.y : d.y;
          return (
            <g key={d.task.id} data-testid="eis-dot" data-task-id={d.task.id}>
              <circle
                cx={sx(x)}
                cy={sy(y)}
                r={d.r}
                tabIndex={0}
                role="button"
                aria-label={`${d.task.title}: ${xProp} ${x}, ${yProp} ${Math.round(y * 10) / 10}`}
                fill={colourOf(x, y)}
                fillOpacity="0.85"
                stroke="var(--surface)"
                strokeWidth="1.5"
                style={{ cursor: draggable ? "grab" : "pointer" }}
                onPointerDown={(e) => {
                  if (!draggable) return;
                  (e.target as Element).setPointerCapture(e.pointerId);
                  setHeld(fitted);
                  setDrag({ id: d.task.id, x: d.x, y: d.y });
                }}
                onClick={() => !drag && ctx.openTask(d.task.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") ctx.openTask(d.task.id);
                  if (!draggable) return;
                  const step = e.shiftKey ? 1 : 0.5;
                  if (e.key === "ArrowRight") write(d.task.id, Math.min(xMax, d.x + step), d.y);
                  else if (e.key === "ArrowLeft") write(d.task.id, Math.max(xMin, d.x - step), d.y);
                  else if (e.key === "ArrowUp") write(d.task.id, d.x, Math.min(yMax, d.y + step));
                  else if (e.key === "ArrowDown") write(d.task.id, d.x, Math.max(yMin, d.y - step));
                  else return;
                  e.preventDefault();
                }}
              />
              <text x={sx(x) + d.r + 4} y={sy(y) + 4} fontSize="11" fill="currentColor" pointerEvents="none">
                {d.task.title.slice(0, 28)}
              </text>
            </g>
          );
        })}
      </svg>
      {axisColours && (
        <AxisLegend
          axes={[
            { label: config.xLabel || xProp, ...axisColours[0] },
            { label: config.yLabel || yProp, ...axisColours[1] },
          ]}
        />
      )}
    </div>
  );
}

const colour: import("../../dashboards/ConfigForm").JsonSchema = { type: "string", "x-drang": "color" };

const numProp: import("../../dashboards/ConfigForm").JsonSchema = {
  type: "string",
  "x-drang": "prop",
  "x-kinds": ["number"],
  "x-computed": true,
};

registerViewType<Eisenhower2Config>({
  id: "eisenhower2",
  label: "views.eisenhower2.label",
  icon: "scatter-chart",
  level: "standard",
  defaultConfig: {
    xProp: "energy",
    xMin: 1,
    xMax: 5,
    yProp: "want",
    yMin: 0,
    yMax: 3,
    dotSize: "estimate",
    colorBy: "axes",
  },
  configSchema: {
    type: "object",
    properties: {
      xProp: { ...numProp, title: "config.xProp" },
      xMin: { type: "number", title: "config.xMin" },
      xMax: { type: "number", title: "config.xMax" },
      xFit: { type: "boolean", title: "config.fitToData" },
      xLabel: { type: "string", title: "config.xLabel" },
      yProp: { ...numProp, title: "config.yProp" },
      yMin: { type: "number", title: "config.yMin" },
      yMax: { type: "number", title: "config.yMax" },
      yFit: { type: "boolean", title: "config.fitToData" },
      yLabel: { type: "string", title: "config.yLabel" },
      quadrants: { type: "array", title: "config.quadrants" },
      dotSize: { type: "string", title: "config.dotSize", enum: ["estimate", "fixed"] },
      colorBy: { type: "string", title: "config.colorBy", enum: ["axes", "none"] },
      xColorMin: { ...colour, default: AXIS_DEFAULTS.x.low, title: "config.xColorMin" },
      xColorMax: { ...colour, default: AXIS_DEFAULTS.x.high, title: "config.xColorMax" },
      yColorMin: { ...colour, default: AXIS_DEFAULTS.y.low, title: "config.yColorMin" },
      yColorMax: { ...colour, default: AXIS_DEFAULTS.y.high, title: "config.yColorMax" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: Eisenhower2View,
  minSize: { w: 4, h: 5 },
});
